import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const snackbarVariants = cva(
  "flex min-w-[320px] max-w-[420px] items-center gap-3.5 rounded-[var(--tb-radius-sm)] bg-tb-oxide px-4 py-3.5 text-tb-porcelain shadow-[var(--tb-shadow-lg)]",
  {
    variants: {
      variant: {
        // green rule on the left — confirms success (e.g. added to the bag)
        success: "shadow-[inset_3px_0_0_var(--tb-green),var(--tb-shadow-lg)]",
        // neutral record — reversible action (e.g. a removal with "Desfazer")
        neutral: "",
        // Local addition to the DS, which told success from neutral only: a rule in
        // `--tb-error`, so a write that failed does not arrive framed like one that worked.
        // Registered in ds-variant-candidates.md (T11).
        error: "shadow-[inset_3px_0_0_var(--tb-error),var(--tb-shadow-lg)]",
      },
    },
    defaultVariants: { variant: "neutral" },
  },
);

export interface SnackbarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof snackbarVariants> {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Optional thumbnail (the item's photo), rendered in a 40x50 slot. */
  thumbnail?: React.ReactNode;
  /** Action on the right (e.g. "Ver sacola" / "Desfazer"). */
  action?: { label: React.ReactNode; onClick?: () => void };
}

/**
 * Presentational only: the live region belongs to the host (`src/components/ui/toast.tsx`).
 *
 * `role="status"` and `aria-live` deliberately left out, against the DS item: declared per
 * instance, a host showing two notices gets two live regions, and the announcement is lost
 * when the region is born together with the text. Tracked in ds-variant-candidates.md (T3).
 */
export const Snackbar = React.forwardRef<HTMLDivElement, SnackbarProps>(
  ({ className, variant, title, description, thumbnail, action, ...props }, ref) => (
    <div ref={ref} className={cn(snackbarVariants({ variant, className }))} {...props}>
      {thumbnail != null && (
        <div className="h-[50px] w-10 shrink-0 overflow-hidden rounded-[var(--tb-radius-xs)]">
          {thumbnail}
        </div>
      )}
      <div className="flex-1 leading-tight">
        <b className="block text-[12.5px] font-medium">{title}</b>
        {description != null && (
          <span className="text-xs opacity-70">{description}</span>
        )}
      </div>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="cursor-pointer whitespace-nowrap text-[12.5px] font-medium text-tb-porcelain"
        >
          <span className="border-b border-current pb-px">{action.label}</span>
        </button>
      )}
    </div>
  ),
);
Snackbar.displayName = "Snackbar";
